import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../../context/WizardContext';
import WizardProgress from '../../components/WizardProgress';
import { createMapping, updateMapping, getPDFFields, getDataSourceData, listDataSources, DataSource } from '../../api/client';
import '../MappingWizard.css';

const Step5Mapping: React.FC = () => {
  const navigate = useNavigate();
  const { state, updateState, saveDraft } = useWizard();
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfFields, setPdfFields] = useState<string[]>([]);
  const [dataSourceFields, setDataSourceFields] = useState<Record<string, string[]>>({});
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, Record<string, string>>>(
    state.fieldMappings || {}
  );
  const [filenamePattern, setFilenamePattern] = useState<string>(state.filenamePattern || '');

  useEffect(() => {
    loadFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFields = async () => {
    setLoading(true);
    try {
      // Load PDF fields
      if (state.pdfTemplateId) {
        const pdfData = await getPDFFields(state.pdfTemplateId);
        const fields = pdfData.fields.map((f) => f.name);
        setPdfFields(fields);
      }

      // Load all data sources
      const allSources = await listDataSources();
      setDataSources(allSources);

      // Load fields from each data source
      const fieldsMap: Record<string, string[]> = {};
      for (const ds of state.dataSources) {
        try {
          const response = await getDataSourceData(ds.id);
          // Use column_order if available, otherwise fall back to Object.keys
          if (response.column_order && response.column_order.length > 0) {
            fieldsMap[ds.id] = response.column_order;
          } else if (response.data) {
            fieldsMap[ds.id] = Object.keys(response.data);
          } else {
            fieldsMap[ds.id] = [];
          }
        } catch (err) {
          // Error silently handled
          fieldsMap[ds.id] = [];
        }
      }
      setDataSourceFields(fieldsMap);

      // Initialize empty mappings if not present
      const initialMappings: Record<string, Record<string, string>> = {};
      state.dataSources.forEach((ds) => {
        if (!fieldMappings[ds.id]) {
          initialMappings[ds.id] = {};
        } else {
          initialMappings[ds.id] = fieldMappings[ds.id];
        }
      });
      setFieldMappings(initialMappings);
    } catch (err) {
      // Error silently handled
    } finally {
      setLoading(false);
    }
  };

  const handleFieldMapping = (sourceId: string, dataField: string, pdfField: string) => {
    setFieldMappings({
      ...fieldMappings,
      [sourceId]: {
        ...fieldMappings[sourceId],
        [dataField]: pdfField,
      },
    });
  };

  const removeMapping = (sourceId: string, dataField: string) => {
    const newMappings = { ...fieldMappings };
    delete newMappings[sourceId][dataField];
    setFieldMappings(newMappings);
  };

  const getDataSourceName = (id: string) => {
    return dataSources.find((ds) => ds.id === id)?.name || id;
  };

  const handleSave = async () => {
    if (!state.pdfTemplateId) {
      alert('Fehler: Kein PDF-Formular ausgewählt');
      return;
    }

    // Update state with field mappings and filename pattern
    updateState({ fieldMappings, filenamePattern });

    setSaving(true);
    try {
      const mappingData = {
        name: state.mappingName,
        description: state.mappingDescription,
        pdf_template_id: state.pdfTemplateId,
        data_sources: state.dataSources,
        relationships: state.relationships,
        field_mappings: fieldMappings,
        filename_pattern: filenamePattern,
        is_draft: false,
        current_step: 5,
      };

      if (state.mappingId) {
        // Update existing draft → finalize
        await updateMapping(state.mappingId, mappingData);
      } else {
        await createMapping(mappingData);
      }

      alert('Projekt erfolgreich gespeichert!');
      navigate('/projects');
    } catch (err: any) {
      alert(`Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/wizard/relationships');
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveDraft(5, { fieldMappings, filenamePattern });
      alert('Entwurf gespeichert!');
    } catch (err: any) {
      alert(`Fehler beim Speichern des Entwurfs: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingDraft(false);
    }
  };

  if (loading) {
    return (
      <>
        <WizardProgress currentStep={5} totalSteps={5} />
        <div className="wizard-step">
          <h2>Feldzuordnung</h2>
          <p>Lade Felder...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <WizardProgress currentStep={5} totalSteps={5} />
      <div className="wizard-step">
        <h2>Feldzuordnung</h2>
        <p className="step-description">
          Ordne die Felder der Datenvorlage den Formularfeldern zu. Ein Feld kann mehreren Formularfeldern zugeordnet werden.
        </p>

        <div className="info-box" style={{ marginBottom: '2rem' }}>
          <strong>📄 PDF-Felder verfügbar:</strong> {pdfFields.length}
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
            {pdfFields.slice(0, 5).join(', ')}
            {pdfFields.length > 5 && ` ... (+${pdfFields.length - 5} weitere)`}
          </div>
        </div>

        {state.dataSources.map((ds) => {
          const dsFields = dataSourceFields[ds.id] || [];
          const dsName = getDataSourceName(ds.id);
          const mappings = fieldMappings[ds.id] || {};

          return (
            <div key={ds.id} style={{ marginBottom: '2rem' }}>
              <h3>
                {dsName}
                <span className={`badge badge-${ds.type}`} style={{ marginLeft: '0.5rem' }}>
                  {ds.type === 'master' ? 'Stammdaten' : 'Detaildaten'}
                </span>
                {ds.role === 'primary' && (
                  <span className="badge badge-primary" style={{ marginLeft: '0.5rem' }}>
                    Primär
                  </span>
                )}
              </h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {dsFields.length} Felder gefunden
              </p>

              {dsFields.length === 0 ? (
                <p className="no-data">
                  Keine Felder gefunden. Bitte prüfe die Datenvorlage.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {dsFields.map((dataField) => {
                    const mappedTo = mappings[dataField];

                    return (
                      <div
                        key={dataField}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '1rem',
                          background: mappedTo ? '#f0f4ff' : '#f9f9f9',
                          borderRadius: '4px',
                          border: mappedTo ? '2px solid #667eea' : '1px solid #e0e0e0',
                        }}
                      >
                        <div style={{ flex: '0 0 200px', fontWeight: 600 }}>
                          {dataField}
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ color: '#999' }}>→</span>
                          <select
                            value={mappedTo || ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                handleFieldMapping(ds.id, dataField, e.target.value);
                              } else {
                                removeMapping(ds.id, dataField);
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                            }}
                          >
                            <option value="">-- Kein Mapping --</option>
                            {pdfFields.map((pdfField) => (
                              <option key={pdfField} value={pdfField}>
                                {pdfField}
                              </option>
                            ))}
                          </select>
                          {mappedTo && (
                            <button
                              onClick={() => removeMapping(ds.id, dataField)}
                              style={{
                                padding: '0.5rem',
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                              }}
                              title="Mapping entfernen"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <h3>Dateinamen-Muster (Optional)</h3>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Definiere ein Muster für die generierten PDF-Dateinamen. Verwende Platzhalter für Datenfelder.
          </p>
          <input
            type="text"
            value={filenamePattern}
            onChange={(e) => setFilenamePattern(e.target.value)}
            placeholder="z.B. Bescheinigung_{Name}_{date}"
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontFamily: 'monospace',
            }}
          />
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
            <strong>Verfügbare Platzhalter:</strong>
            <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
              <li><code>{'{feldname}'}</code> - Ersetze "feldname" mit einem Datenfeld (z.B. <code>{'{Name}'}</code>, <code>{'{Email}'}</code>)</li>
              <li><code>{'{date}'}</code> - Aktuelles Datum (YYYY-MM-DD)</li>
              <li><code>{'{datetime}'}</code> - Datum und Uhrzeit</li>
              <li><code>{'{timestamp}'}</code> - Unix-Timestamp</li>
            </ul>
          </div>
        </div>

        <div className="wizard-actions">
          <button onClick={handleBack} className="btn-secondary">
            ← Zurück
          </button>
          <button
            onClick={handleSaveDraft}
            className="btn-secondary"
            disabled={savingDraft || saving}
            style={{ marginLeft: 'auto' }}
          >
            {savingDraft ? 'Speichere...' : 'Als Entwurf speichern'}
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving || savingDraft}>
            {saving ? 'Speichere...' : 'Projekt speichern'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Step5Mapping;
